import { format } from "date-fns";
import { GetReportDataResponse } from "src/services/restApi/queries/report.service";
import {
  AggregatedWithdrawalData,
  Grouping,
  RawData,
  Withdrawal,
} from "../types";
import { TRADES_GROUP_BY_OPTIONS } from "./consts";
import { capitaliseFirstLetter, centsToDollars } from "./utils";

export function getWithdrawalGroupOptions(
  withdrawals: RawData<Withdrawal>,
  group: Grouping
) {
  const uniqueValues = new Set<string>();
  withdrawals.forEach((withdrawal) => {
    const date = new Date(withdrawal.createdAt);
    switch (group.groupBy) {
      case "psp":
        uniqueValues.add(withdrawal.psp || "Unknown PSP");
        break;
      case "client":
        uniqueValues.add(withdrawal.userId || "Unknown Client");
        break;
      case "time":
        if (group.timeGroup === "month") {
          uniqueValues.add(format(date, "MMMM yyyy"));
        } else if (group.timeGroup === "week") {
          uniqueValues.add(format(date, "yyyy-'W'ww"));
        } else if (group.timeGroup === "day") {
          uniqueValues.add(format(date, "yyyy-MM-dd"));
        }
        break;
    }
  });
  return Array.from(uniqueValues).sort();
}

export function aggregateWithdrawalData(
  groupings: Grouping[],
  withdrawals: RawData<Withdrawal>
): AggregatedWithdrawalData[] {
  const groupedData = new Map<string, AggregatedWithdrawalData>();

  withdrawals.forEach((withdrawal) => {
    const groupKeyParts = groupings.map((g) => {
      const date = new Date(withdrawal.createdAt);
      switch (g.groupBy) {
        case "psp":
          return withdrawal.psp || "Unknown PSP";
        case "time":
          if (g.timeGroup === "month") {
            return format(date, "MMMM yyyy") || "Unknown Time";
          } else if (g.timeGroup === "week") {
            return format(date, "yyyy-'W'ww") || "Unknown Time";
          } else if (g.timeGroup === "day") {
            return format(date, "yyyy-MM-dd") || "Unknown Time";
          }
          return "Unknown Time";
        case "client":
        default:
          return withdrawal.userId || "Unknown Client";
      }
    });
    const groupKey = groupKeyParts.join(" + ");
    if (!groupedData.has(groupKey)) {
      groupedData.set(groupKey, {
        userId: withdrawal.userId || "",
        psp: withdrawal.psp || "",
        numberOfWithdrawals: 0,
        amount: 0,
        groupKey,
        groupValues: groupKeyParts,
        withdrawals: [],
      });
    }
    const group = groupedData.get(groupKey);
    if (!group) return;

    group.withdrawals.push(withdrawal);
    group.numberOfWithdrawals++;
    group.amount += centsToDollars(withdrawal.amount);
  });
  return Array.from(groupedData.values());
}

export function createWithdrawalsCsv(
  groupings: Grouping[],
  data: GetReportDataResponse,
  aggregatedData: AggregatedWithdrawalData[]
): string {
  let csvContent = "";
  if (groupings.length === 0 && data.data) {
    const headers = [
      "Withdrawal ID",
      "Client ID",
      "Withdrawal Status",
      "PSP",
      "PSP Transaction ID",
      "Currency Type",
      "Amount (USD)",
      "Local Amount",
      "Local Currency",
      "Exchange Rate",
      "Created At",
      "Updated At",
      "Admin Action",
      "Admin Reason",
      "Admin ID",
    ];

    csvContent = [
      headers.join(","),
      ...(data.data as RawData<Withdrawal>).map((withdrawal) => {
        return [
          `${withdrawal.withdrawalId.toLocaleString()}`,
          `${withdrawal.userId.toLocaleString()}`,
          `${capitaliseFirstLetter(
            withdrawal.transactionStatus.toLocaleString().toLowerCase()
          )}`,
          `${withdrawal.psp || "-"}`,
          `${withdrawal.pspTransactionId || "-"}`,
          `${withdrawal.currencyType || "-"}`,
          `${withdrawal.amount / 100}`,
          `${(withdrawal.localAmount || 0) / 100}`,
          `${withdrawal.localCurrency || "-"}`,
          `${withdrawal.exchangeRate || "-"}`,
          `${new Date(withdrawal.createdAt).toISOString()}`,
          `${
            withdrawal.updatedAt
              ? new Date(withdrawal.updatedAt).toISOString()
              : "-"
          }`,
          `${withdrawal.adminAction ?? "-"}`,
          `${withdrawal.adminReason ?? "-"}`,
          `${withdrawal.adminId ?? "-"}`,
        ].join(",");
      }),
    ].join("\n");
  } else {
    // Get the filtered data
    const filteredData = aggregatedData.filter((row) => {
      const keyParts = row.groupKey.split(" + ");
      return groupings.every((g, idx) => {
        const filter: string[] = g.groupFilter;
        if (!filter || filter.length === 0) return true;
        return filter.includes(keyParts[idx]);
      });
    });

    // Sort filteredData by all group columns (groupValues)
    filteredData.sort((a, b) => {
      for (let i = 0; i < a.groupValues.length; i++) {
        if (a.groupValues[i] < b.groupValues[i]) return -1;
        if (a.groupValues[i] > b.groupValues[i]) return 1;
      }
      return 0;
    });

    // Prepare headers
    const headers = [
      ...groupings.map(
        (g) =>
          TRADES_GROUP_BY_OPTIONS.find((opt) => opt.value === g.groupBy)
            ?.name || g.groupBy
      ),
      "Number of Withdrawals",
      "Amount (USD)",
    ];

    // Prepare CSV content
    csvContent = [
      headers.join(","),
      ...filteredData.map((row) => {
        return [
          ...row.groupValues.map((val) => `"${val}"`),
          `"${row.numberOfWithdrawals.toLocaleString()}"`,
          `"$${row.amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}"`,
        ].join(",");
      }),
    ].join("\n");
  }

  return csvContent;
}
